import React, { useState } from "react";
import {
    TextField,
    PrimaryButton,
    Stack,
    Label,
    mergeStyleSets,
    Dialog,
    DialogFooter,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import { axiosInstance } from '../../../../api/config';
import FeatureSummary from "../../../Util/FeatureSummary";
import env from "../../../../env";
const { REACT_APP_API_BASE_URI } = env;

const styles = mergeStyleSets({
    formRow: {
        marginBottom: "20px",
        display: "flex",
        alignItems: "center",
    },
    label: {
        flex: "1 0 25%",
    },
    input: {
        flex: "1 0 75%",
    },
    buttonRow: {
        display: "flex",
        justifyContent: "flex-end",
    },
    successMessage: {
        backgroundColor: "#90EE90",
        fontWeight: "bold",
    },
    errorMessage: {
        backgroundColor: "#FFCCCC",
        fontWeight: "bold",
    },
    spinnerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100px',
    }
});

const UserMerge = () => {
    const [userToMerge, setUserToMerge] = useState("");
    const [mergeUserCobrandId, setMergeUserCobrandId] = useState("");
    const [userToKeep, setUserToKeep] = useState("");
    const [keepUserCobrandId, setKeepUserCobrandId] = useState("");
    const [responseMessage, setResponseMessage] = useState(null);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [isLoadingRequest, setIsLoadingRequest] = useState(false);

    const [{ loading, error }, execute] = useAxios(
        {
            url: `2020-05-05/user/user-merge`,
            method: "POST",
            baseURL: REACT_APP_API_BASE_URI,
        },
        { axios: axiosInstance, manual: true }
    );

    const validateInputEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
    };

    const validateInputNumber = (value) => {
        const numberRegex = /^\d{1,4}$/;
        return numberRegex.test(value);
    };

    const validateInputs = () => {
        const errors = {};

        if (!userToMerge) {
            errors.userToMerge = "This field is required.";
        } else if (!validateInputEmail(userToMerge)) {
            errors.userToMerge = "Invalid input (Valid email required)";
        }

        if (!mergeUserCobrandId) {
            errors.mergeUserCobrandId = "This field is required.";
        } else if (!validateInputNumber(mergeUserCobrandId)) {
            errors.mergeUserCobrandId = "Invalid input (Numeric value required) and no more than 4 digits.";
        }

        if (!userToKeep) {
            errors.userToKeep = "This field is required.";
        } else if (!validateInputEmail(userToKeep)) {
            errors.userToKeep = "Invalid input (Valid email required)";
        }

        if (!keepUserCobrandId) {
            errors.keepUserCobrandId = "This field is required.";
        } else if (!validateInputNumber(keepUserCobrandId)) {
            errors.keepUserCobrandId = "Invalid input (Numeric value required) and no more than 4 digits.";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleMergeClick = () => {
        if (validateInputs()) {
            setIsDialogVisible(true);
        }
    };

    const handleSubmit = async () => {
        setIsDialogVisible(false);
        setIsLoadingRequest(true);
        setResponseMessage(null);

        const payload = {
            userMerge: userToMerge,
            userKeep: userToKeep,
            userToMergeCobrandId: mergeUserCobrandId,
            userToKeepCobrandId: keepUserCobrandId,
        };

        try {
            const response = await execute({
                data: payload,
                headers: { "Content-Type": "application/json" }
            });
            setResponseMessage(response.data);
        } catch (err) {
            console.error("Error merging users:", err);
            setResponseMessage(err.message || "An error occurred.");
        } finally {
            setIsLoadingRequest(false);
        }
    };

    return (
        <div style={{ padding: '0 0 0 20px' }}>
            <h3>User Merge</h3>
            <div style={{ width: 400 }}>
                <Stack tokens={{ childrenGap: 0 }}>
                    <div className={styles.formRow}>
                        <Label className={styles.label}>Username to merge</Label>
                        <TextField
                            className={styles.input}
                            value={userToMerge}
                            onChange={(e, newValue) => setUserToMerge(newValue || "")}
                            errorMessage={validationErrors.userToMerge}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <Label className={styles.label}>Cobrand ID of user to merge</Label>
                        <TextField
                            className={styles.input}
                            value={mergeUserCobrandId}
                            onChange={(e, newValue) => setMergeUserCobrandId(newValue || "")}
                            errorMessage={validationErrors.mergeUserCobrandId}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <Label className={styles.label}>Username to keep</Label>
                        <TextField
                            className={styles.input}
                            value={userToKeep}
                            onChange={(e, newValue) => setUserToKeep(newValue || "")}
                            errorMessage={validationErrors.userToKeep}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <Label className={styles.label}>Cobrand ID of user to keep</Label>
                        <TextField
                            className={styles.input}
                            value={keepUserCobrandId}
                            onChange={(e, newValue) => setKeepUserCobrandId(newValue || "")}
                            errorMessage={validationErrors.keepUserCobrandId}
                        />
                    </div>
                    <div className={styles.buttonRow}>
                        <PrimaryButton onClick={handleMergeClick}>Merge Users</PrimaryButton>
                    </div>
                </Stack>
            </div >

            <Dialog
                hidden={!isDialogVisible}
                onDismiss={() => setIsDialogVisible(false)}
                dialogContentProps={{
                    title: "Are you absolutely sure you want to merge users?",
                }}
            >
                <div style={{ padding: "20px" }}>
                    {userToMerge} with cobrand ID {mergeUserCobrandId} will be lost.<br /><br />
                    {userToKeep} with cobrand ID {keepUserCobrandId} will remain.
                </div>
                <DialogFooter>
                    <DefaultButton onClick={() => setIsDialogVisible(false)} text="Cancel" />
                    <PrimaryButton onClick={handleSubmit} text="OK" />
                </DialogFooter>
            </Dialog>
            <br />
            {
                isLoadingRequest && (
                    <div className={styles.spinnerContainer}>
                        <Spinner size={SpinnerSize.large} label="Processing request..." ariaLive="assertive" />
                    </div>
                )
            }

            {
                responseMessage && !isLoadingRequest && (
                    <MessageBar messageBarType={responseMessage.includes("Kept") ? MessageBarType.success : MessageBarType.error}>
                        {responseMessage.includes("Kept") ? "Successfully merged users " + responseMessage : responseMessage}
                    </MessageBar>
                )
            }
            <FeatureSummary
                label="About User Merge"
                summary="Merge two user accounts into one. The 'user to merge' will be deleted and their data transferred to the 'user to keep'. Both usernames (emails) and cobrand IDs are required. This action cannot be undone."
            />
        </div >
    );
};

export default UserMerge;